import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Spin,
  Alert,
  Typography,
  Space,
  Card,
  Row,
  Col,
  Result,
  Modal,
  Input
} from "antd";
import {
  downloadOrderItemFile,
  getOrderItemsByEmployeeId,
  getAssignedOrderItemsByEmployeeId,
  updateOrderItemStatus,
  updateOrderItemNote,
} from "../../../services/orderItemService";
import { FilePdfOutlined, ReloadOutlined, UserOutlined, EditOutlined } from "@ant-design/icons";
import { useApiCall } from "../../../hooks/useApiCall";
import { toast } from 'react-toastify';
import StatusSelect from "../../../components/common/StatusSelect";

const { Title } = Typography;

const EmployeeOrderItemsPage = () => {
  const employeeId = localStorage.getItem("userId");
  const [orderItems, setOrderItems] = useState([]);
  const [viewMode, setViewMode] = useState("all"); // "all" or "assigned"
  const [editingItem, setEditingItem] = useState(null);
  const [editNoteValue, setEditNoteValue] = useState("");
  const [noteUpdateLoading, setNoteUpdateLoading] = useState(false);
  
  // Use separate hooks for different operations
  const { loading: allItemsLoading, error: allItemsError, execute: executeAllItemsCall } = useApiCall();
  const { loading: assignedItemsLoading, error: assignedItemsError, execute: executeAssignedItemsCall } = useApiCall();
  const { loading: statusUpdateLoading, execute: executeStatusUpdate } = useApiCall();
  const { loading: downloadLoading, execute: executeDownload } = useApiCall();


  useEffect(() => {
    fetchOrderAndItems();
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrderAndItems = async () => {
    if (!employeeId) {
      return;
    }

    try {
      const itemsResponse = await executeAllItemsCall(
        () => getOrderItemsByEmployeeId(employeeId)
      );

      setOrderItems(itemsResponse?.content || []);
      setViewMode("all");
    } catch (err) {
      console.error("Error fetching all order items:", err);
      setOrderItems([]);
    }
  };

  const fetchAssignedItems = async () => {
    if (!employeeId) {
      return;
    }

    try {
      const assignedItemsResponse = await executeAssignedItemsCall(
        () => getAssignedOrderItemsByEmployeeId(employeeId)
      );
      
      setOrderItems(assignedItemsResponse?.content || []);
      setViewMode("assigned");
    } catch (err) {
      console.error("Error fetching assigned items:", err);
      setOrderItems([]);
    }
  };

  const handleDownloadFile = async (itemId) => {
    try {
      await executeDownload(
        () => downloadOrderItemFile(itemId)
      );
      
      toast.success("Dosya başarıyla indirildi!");
    } catch (err) {
      console.error("File download error:", err);
      // Error is already handled by the hook
    }
  };

  const handleItemStatusChange = async (itemId, newStatus) => {
    try {
      await executeStatusUpdate(
        () => updateOrderItemStatus(itemId, newStatus)
      );
      
      // Update local state
      setOrderItems((items) =>
        items.map((item) =>
          item.id === itemId ? { ...item, status: newStatus } : item
        )
      );
      
      toast.success("Durum başarıyla güncellendi!");
    } catch (err) {
      console.error("Status update error:", err);
      // Error is already handled by the hook
    }
  };

  const handleUpdateNote = async () => {
    setNoteUpdateLoading(true);
    try {
      await updateOrderItemNote(editingItem.id, employeeId, editNoteValue);
      setOrderItems((items) =>
        items.map((item) =>
          item.id === editingItem.id ? { ...item, notes: editNoteValue } : item
        )
      );
      toast.success("Not başarıyla güncellendi!");
      setEditingItem(null);
    } catch (err) {
      console.error("Note update error:", err);
      toast.error("Not güncellenirken hata oluştu");
    } finally {
      setNoteUpdateLoading(false);
    }
  };

  const handleRetryAll = () => {
    fetchOrderAndItems();
  };

  const handleRetryAssigned = () => {
    fetchAssignedItems();
  };

  const columns = [
    {
      title: "Sipariş Referansı",
      dataIndex: "orderRef",
      key: "orderRef",
    },
    {
      title: "Şirket Adı",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Kalem İsmi",
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: "Adet",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Atanan Kişi",
      dataIndex: "assignedEmployeeName",
      key: "assignedEmployeeName",
      render: (name) => name || "-",
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <StatusSelect
          type="orderItem"
          value={status}
          onChange={(value) => handleItemStatusChange(record.id, value)}
          loading={statusUpdateLoading}
          disabled={statusUpdateLoading}
        />
      ),
    },
    {
      title: "Notlar",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => notes || "-",
    },
    {
      title: "Dosya",
      dataIndex: "hasFile",
      key: "hasFile",
      render: (hasFile, record) =>
        hasFile ? (
          <Button
            type="text"
            icon={<FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />}
            onClick={() => handleDownloadFile(record.id)}
            loading={downloadLoading}
            disabled={downloadLoading}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Eylemler",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingItem(record);
            setEditNoteValue(record.notes || "");
          }}
        >
          Notu Düzenle
        </Button>
      ),
    },
  ];

  // Handle missing employee ID
  if (!employeeId) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="warning"
          title="Çalışan Bilgisi Bulunamadı"
          subTitle="Lütfen tekrar giriş yapın."
          extra={
            <Button type="primary" onClick={() => window.location.href = "/employee/login"}>
              Giriş Yap
            </Button>
          }
        />
      </div>
    );
  }

  // Handle major errors when both views fail
  const currentError = viewMode === "all" ? allItemsError : assignedItemsError;
  const currentLoading = viewMode === "all" ? allItemsLoading : assignedItemsLoading;
  
  if (currentError && !currentLoading && orderItems.length === 0) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="error"
          title="İşler Yüklenemedi"
          subTitle={`${viewMode === "all" ? "Tüm işler" : "Atanan işler"} yüklenirken bir hata oluştu. Lütfen tekrar deneyin.`}
          extra={[
            <Button 
              type="primary" 
              key="retry" 
              icon={<ReloadOutlined />} 
              onClick={viewMode === "all" ? handleRetryAll : handleRetryAssigned}
            >
              Tekrar Dene
            </Button>,
            <Button 
              key="switch" 
              onClick={viewMode === "all" ? fetchAssignedItems : fetchOrderAndItems}
            >
              {viewMode === "all" ? "Atanan İşleri Görüntüle" : "Tüm İşleri Görüntüle"}
            </Button>
          ]}
        />
      </div>
    );
  }

  // Show loading state
  if (currentLoading && orderItems.length === 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "5rem" }}>
        <Spin tip="İşler yükleniyor..." size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              <UserOutlined style={{ marginRight: 8 }} />
              İşlerim
            </Title>
            <Space>
              <Button 
                type={viewMode === "all" ? "primary" : "default"}
                onClick={fetchOrderAndItems}
                loading={allItemsLoading}
                disabled={allItemsLoading || assignedItemsLoading}
              >
                Tüm İşler
              </Button>
              <Button 
                type={viewMode === "assigned" ? "primary" : "default"}
                onClick={fetchAssignedItems}
                loading={assignedItemsLoading}
                disabled={allItemsLoading || assignedItemsLoading}
              >
                Bana Atanan İşler
              </Button>
            </Space>
          </div>
        }
      >
        {/* Show warning if there's an error but we have cached data */}
        {currentError && orderItems.length > 0 && (
          <Alert
            message="Veriler güncel olmayabilir"
            description="Son güncellemede bir hata oluştu, gösterilen veriler eski olabilir."
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            action={
              <Button 
                size="small" 
                onClick={viewMode === "all" ? handleRetryAll : handleRetryAssigned}
              >
                Yenile
              </Button>
            }
          />
        )}

        {/* Show loading overlay for refresh */}
        {currentLoading && orderItems.length > 0 && (
          <Alert
            message="Yenileniyor..."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {orderItems.length === 0 && !currentLoading ? (
          <Alert
            message={viewMode === "all" ? "Henüz hiç iş bulunamadı" : "Size atanmış iş bulunamadı"}
            description={viewMode === "all" ? 
              "Henüz size herhangi bir iş atanmamış." : 
              "Size özel olarak atanmış bir iş bulunmuyor."
            }
            type="info"
            showIcon
            action={
              <Button onClick={viewMode === "all" ? fetchAssignedItems : fetchOrderAndItems}>
                {viewMode === "all" ? "Atanan İşleri Görüntüle" : "Tüm İşleri Görüntüle"}
              </Button>
            }
          />
        ) : (
          <Table
            dataSource={orderItems}
            columns={columns}
            rowKey="id"
            pagination={false}
            scroll={{ x: true }}
            footer={() => (
              <Row justify="end">
                <Col>
                  <Typography.Text strong>
                    Toplam Ürün: {orderItems.length}
                  </Typography.Text>
                </Col>
              </Row>
            )}
          />
        )}

        {/* Show status update feedback */}
        {statusUpdateLoading && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'white',
            padding: '12px 16px',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #d9d9d9'
          }}>
            <Space>
              <Spin size="small" />
              <Typography.Text>Durum güncelleniyor...</Typography.Text>
            </Space>
          </div>
        )}
      </Card>

      {/* Note Edit Modal */}
      <Modal
        title="Not Düzenle"
        open={!!editingItem}
        onCancel={() => setEditingItem(null)}
        onOk={handleUpdateNote}
        confirmLoading={noteUpdateLoading}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Input.TextArea
          value={editNoteValue}
          onChange={(e) => setEditNoteValue(e.target.value)}
          rows={4}
          placeholder="Notunuzu yazın..."
          maxLength={1000}
          showCount
        />
      </Modal>
    </div>
  );
};

export default EmployeeOrderItemsPage;